// Iter 76 — Per-vendor Payments tab on VendorDetail (read-only view).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CurrencyInr, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatAmount } from "./vendor_constants";

export default function VendorPaymentsTab({ vendor }) {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get(`/vendor-payments`, { params: { vendor_id: vendor.id, page_size: 100, sort: "payment_date_desc" } })
            .then(({ data }) => { if (!cancelled) setRows(data.items || []); })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err, "Failed to load payments")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [vendor.id]);

    if (loading) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="vendor-payments-loading">Loading…</div>;

    const totalPaid = rows.reduce((acc, p) => acc + Number(p.amount || 0), 0);

    return (
        <div data-testid="vendor-payments-tab">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-medium text-slate-900">Payments</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        All payments to this vendor. To record a new payment, open a bill and use the &quot;Record Payment&quot; action.
                    </p>
                </div>
                {rows.length > 0 && (
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                        Total paid: {formatAmount(totalPaid, vendor.currency || "INR")}
                    </Badge>
                )}
            </div>
            {rows.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg" data-testid="vendor-payments-empty">
                    <CurrencyInr size={28} className="text-slate-300 mx-auto mb-2" />
                    <div className="text-sm text-slate-500">No payments recorded</div>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="vendor-payments-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Date</th>
                                <th className="text-left font-medium px-4 py-3">Bill</th>
                                <th className="text-left font-medium px-4 py-3">Mode</th>
                                <th className="text-left font-medium px-4 py-3">Reference</th>
                                <th className="text-right font-medium px-4 py-3">Amount</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => (
                                <tr key={p.id} data-testid={`vendor-payment-row-${p.code}`}
                                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => navigate(`/vendor-bills/${p.vendor_bill_id}`)}>
                                    <td className="px-4 py-3 font-mono text-xs">{p.code}</td>
                                    <td className="px-4 py-3 text-xs text-slate-700">{formatDate(p.payment_date)}</td>
                                    <td className="px-4 py-3 text-xs">
                                        <div className="font-mono text-slate-700">{p.vendor_bill_code}</div>
                                        <div className="text-slate-500 text-[11px]">{p.vendor_bill_number}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100">{p.payment_mode}</Badge>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{p.reference_number || "—"}</td>
                                    <td className="px-4 py-3 text-right font-mono text-slate-900">{formatAmount(p.amount, p.currency)}</td>
                                    <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
